import { create } from "zustand";
import { persist } from "zustand/middleware";
import { MOCK_TRANSFERS, MOCK_TRANSFER_DETAILS, MOCK_APPROVALS, MOCK_APPROVAL_DETAILS } from "@/data/mock";
import type { Approval, ApprovalDetail, Transfer, TransferDetail, TransferRequestValues } from "@/types";

export interface CreateTransferInput {
  values: TransferRequestValues;
  sourceName: string;
  destinationLabel: string;
  isExternalDestination: boolean;
  assetNetwork: string;
  assetRoute: string;
  usdLabel: string;
}

interface TransferState {
  transfers: Transfer[];
  transferDetails: Record<string, TransferDetail>;
  approvals: Approval[];
  approvalDetails: Record<string, ApprovalDetail>;
  counter: number;
  evidenceCounter: number;
  createTransfer: (input: CreateTransferInput) => Transfer;
  approveTransfer: (id: string) => void;
  rejectTransfer: (id: string, reason: string, notes: string, generateEvidence: boolean) => void;
  escalateTransfer: (id: string, reason: string, notes: string) => void;
  holdTransfer: (id: string, reason: string) => void;
}

function nowLabel() {
  const d = new Date();
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

const seedApprovalDetails: Record<string, ApprovalDetail> = {};
for (const d of MOCK_APPROVAL_DETAILS) seedApprovalDetails[d.id] = d;

export const useTransferStore = create<TransferState>()(
  persist(
    (set, get) => ({
      transfers: MOCK_TRANSFERS,
      transferDetails: { ...MOCK_TRANSFER_DETAILS },
      approvals: MOCK_APPROVALS,
      approvalDetails: seedApprovalDetails,
      counter: 7,
      evidenceCounter: 8,
      createTransfer: (input) => {
        const n = get().counter + 1;
        const id = `TXI-${String(n).padStart(3, "0")}`;
        const time = nowLabel();
        const { values } = input;

        const transfer: Transfer = {
          id,
          direction: "send",
          route: `${input.sourceName} → ${input.destinationLabel}`,
          asset: values.asset,
          amount: values.amount,
          status: "pending-approval",
          statusLabel: "Pending Approval",
          statusVariant: "warning",
          created: time,
          creator: "Sai Suman Kanuganti",
          wallet: values.sourceWalletId,
          state: "pending-approval",
        };

        const detail: TransferDetail = {
          id,
          direction: "send",
          amountMain: `${values.amount} ${values.asset}`,
          amountUsd: input.usdLabel,
          source: `${input.sourceName} (${values.sourceWalletId})`,
          destination: input.destinationLabel,
          createdBy: "Sai Suman Kanuganti (USR-002)",
          providerRoute: input.assetRoute,
          policy: "POL-001 — Transfer Policy",
          defaultState: "pending-approval",
          timeline: [
            { title: "Draft Created", meta: `${time} UTC · Sai Suman Kanuganti (USR-002)`, tone: "success" },
            { title: "Policy Evaluation Passed", meta: `${time} UTC · POL-001`, tone: "success" },
          ],
          quorum: {
            label: "Approval Required",
            needed: 1,
            total: 2,
            done: 0,
            approvers: [
              { initials: "MW", name: "Marcus Webb", role: "Security Admin · Assigned", statusLabel: "Pending", statusVariant: "warning" },
              { initials: "EV", name: "Elena Vasquez", role: "Compliance Officer · Alternate", statusLabel: "Available", statusVariant: "neutral" },
            ],
          },
        };

        const approval: Approval = {
          id,
          type: "transfer",
          title: `Transfer: ${values.amount} ${values.asset} → ${input.destinationLabel}`,
          meta: `${id} · Sai Suman Kanuganti · ${time} UTC`,
          hint: "0 of 1 required · Newly submitted",
          slaLeft: "4h 0m left",
          slaSeverity: "ok",
          approvalsRequired: 1,
          approvalsDone: 0,
          progressPercent: null,
          status: "pending",
          urgent: false,
        };

        const approvalDetail: ApprovalDetail = {
          id,
          approvalId: `APR-${String(n).padStart(3, "0")}`,
          transferId: id,
          amount: `${values.amount} ${values.asset} ${input.usdLabel}`,
          source: `${input.sourceName} (${values.sourceWalletId})`,
          destination: input.destinationLabel,
          requester: "Sai Suman Kanuganti (USR-002) · Treasury Operator",
          memo: values.memo,
          risk: "low",
          policyTitle: "POL-001 — Transfer Policy",
          policyDesc: "Amount within policy limit for this route. Approval required: 1-of-2 (Security Admin or Policy Approver).",
          screeningTitle: input.isExternalDestination ? "Screening in progress" : "No screening required",
          screeningDesc: input.isExternalDestination
            ? "External address — screened against sanctions and risk lists before approval can complete."
            : "Internal wallet-to-wallet transfer. Destination is a governed wallet under the same organization.",
          quorumLabel: "Required: 1 of 2 · SLA: 4h remaining",
          quorumPct: 0,
          approvers: [
            { initials: "MW", name: "Marcus Webb (You)", role: "Security Admin · Assigned", status: "action", you: true },
            { initials: "EV", name: "Elena Vasquez", role: "Compliance Officer · Alternate", status: "available" },
          ],
        };

        set((s) => ({
          transfers: [transfer, ...s.transfers],
          transferDetails: { ...s.transferDetails, [id]: detail },
          approvals: [approval, ...s.approvals],
          approvalDetails: { ...s.approvalDetails, [id]: approvalDetail },
          counter: n,
        }));

        return transfer;
      },

      // Approval granted (post-MFA). Only the transfer's own lifecycle state
      // changes — matches the existing seeded example (TXI-005 "approved" →
      // shows a Signing & Broadcast Progress panel). No "processing"/
      // "completed" alias is invented; "approved" is the real state name.
      approveTransfer: (id) => {
        const s = get();
        const detail = s.transferDetails[id];
        if (!s.approvals.find((a) => a.id === id) || !s.transfers.find((t) => t.id === id) || !detail) return;
        const time = nowLabel();
        set({
          approvals: s.approvals.map((a) =>
            a.id === id ? { ...a, status: "approved", approvalsDone: a.approvalsRequired, progressPercent: 100, hint: "Approved — proceeding to signing" } : a
          ),
          transfers: s.transfers.map((t) =>
            t.id === id ? { ...t, status: "signing", statusLabel: "Signing Authorized", statusVariant: "info", state: "approved" } : t
          ),
          transferDetails: {
            ...s.transferDetails,
            [id]: {
              ...detail,
              defaultState: "approved",
              quorum: detail.quorum ? { ...detail.quorum, done: detail.quorum.needed } : detail.quorum,
              signing: {
                heading: "Signing & Broadcast Progress",
                note: "Signing and broadcast occur automatically once approval quorum is met.",
                steps: [
                  { title: "Approval Completed", meta: `${time} UTC · Marcus Webb (USR-005)`, tone: "success" },
                  { title: "Signing Authorized", meta: `${time} UTC · ${detail.providerRoute}`, detail: "Awaiting signature completion", tone: "active" },
                ],
              },
            },
          },
        });
      },

      // Rejection is terminal — matches the existing seeded example (TXI-007).
      rejectTransfer: (id, reason, notes, generateEvidence) => {
        const s = get();
        const detail = s.transferDetails[id];
        if (!s.approvals.find((a) => a.id === id) || !s.transfers.find((t) => t.id === id) || !detail) return;
        const n = s.evidenceCounter + 1;
        set({
          approvals: s.approvals.map((a) => (a.id === id ? { ...a, status: "rejected", hint: `Rejected — ${reason}` } : a)),
          transfers: s.transfers.map((t) =>
            t.id === id ? { ...t, status: "rejected", statusLabel: "Rejected", statusVariant: "danger", state: "rejected" } : t
          ),
          transferDetails: {
            ...s.transferDetails,
            [id]: {
              ...detail,
              defaultState: "rejected",
              rejection: { reason, quote: notes || reason, by: "Marcus Webb, Security Admin" },
              ...(generateEvidence
                ? { evidenceReceipt: { evidenceId: `EVD-${String(n).padStart(3, "0")}`, disposition: "Rejected — no funds moved" } }
                : {}),
            },
          },
          evidenceCounter: generateEvidence ? n : s.evidenceCounter,
        });
      },

      // Escalation reassigns the approval to a senior approver — it does not
      // change the transfer's own lifecycle state (no "escalated" value
      // exists in TransferDetailState/TransferListStatus, and escalation is a
      // routing decision, not a transfer outcome, so leaving the transfer
      // itself unchanged is correct, not a gap).
      escalateTransfer: (id, reason) => {
        const s = get();
        if (!s.approvals.find((a) => a.id === id)) return;
        set({
          approvals: s.approvals.map((a) => (a.id === id ? { ...a, status: "escalated", urgent: true, hint: `Escalated — ${reason}` } : a)),
        });
      },

      // "Hold" has no dedicated status in the existing ApprovalStatus /
      // TransferDetailState enums — inventing one would violate "use only
      // states supported by the existing data model." The approval honestly
      // stays "pending" (a hold doesn't resolve it), reflected only via the
      // existing free-text hint field.
      holdTransfer: (id, reason) => {
        const s = get();
        if (!s.approvals.find((a) => a.id === id)) return;
        set({
          approvals: s.approvals.map((a) => (a.id === id ? { ...a, hint: `On hold — ${reason}` } : a)),
        });
      },
    }),
    { name: "alloy-transfers" }
  )
);
